import { isAppError, REFRESH_FAILED_CODE } from '@cenorion/api';

import { env } from '@/core/env/env';
import { reportError } from '@/core/errors/report-error';
import { i18n } from '@/core/i18n';

import { fetchAuthConfig } from './auth-config';
import {
  getIdleTimeoutMs,
  isIdleExpired,
  readLastActivity,
  setIdleTimeout,
  stampActivity,
} from './idle-timeout';
import { leaveProtectedUi } from './leave-protected-ui';
import { getUserManager, initOidc } from './oidc';
import {
  cacheStoredToken,
  discardStoredSession,
  loadUserProfile,
  readCallbackParams,
  signOutAndRevoke,
  stripOAuthParams,
  stripStrayOAuthParams,
} from './oidc-session';
import { resetRedirectAttempts } from './redirect-breaker';
import { safeReturnTo } from './safe-return-to';
import { setBearerToken, useSessionStore, type SessionUser } from './session-store';

/** Resolves once status leaves `unknown`. Route loaders await this. */
let resolveAuthReady: (() => void) | null = null;
export const authReady: Promise<void> = new Promise<void>((resolve) => {
  resolveAuthReady = resolve;
});

/** True once boot has classified the session. Guards `endSession` — see below. */
let bootSettled = false;

function markReady(): void {
  bootSettled = true;
  resolveAuthReady?.();
  resolveAuthReady = null;
}

// Captured from the login round trip, consumed once by the /auth/callback loader: boot cannot
// navigate (no router yet), so handing the value to a loader keeps the redirect in the router's own
// control flow and the callback route never paints.
let pendingReturnTo: string | null = null;

export function takePendingReturnTo(): string | null {
  const value = pendingReturnTo;
  pendingReturnTo = null;
  return value;
}

/**
 * Terminal auth failure from the API client's 401 handler. While boot runs, only clears state:
 * resolving `authReady` early would publish `anonymous` before boot classifies the failure, letting
 * a guard redirect to Hosted UI and repeat the same loop. After boot, an explicit exit is required
 * too — guards are loaders that only run on navigation.
 *
 * Discards the stored session as well as the in-memory one: the refresh that just failed left its
 * dead token in localStorage, and leaving it there makes every later boot replay the same doomed
 * token request. The bearer is cleared synchronously (the discard only awaits afterwards), so a
 * caller that issues a request in the same tick still sends no stale credential.
 */
export function endSession(cause: unknown): void {
  // Reported on the live→anonymous transition only: every request awaiting the same rejected
  // refresh calls this, and one dead session must not become one Sentry event per sibling 401.
  if (useSessionStore.getState().status === 'authenticated') {
    reportError(cause, { errorKind: 'session_refresh_failed' });
  }
  void discardStoredSession();
  useSessionStore.getState().setAnonymous();
  if (!bootSettled) return;

  markReady();
  void leaveProtectedUi();
}

/**
 * Settles the session before the router mounts. Three entry paths, not two: Cognito can return an
 * `error` with no `code`, and treating that as an ordinary load would silently bounce the user back
 * to the Hosted UI with no explanation.
 */
export async function restoreSession(fetchProfile: () => Promise<SessionUser>): Promise<void> {
  // Runtime branch, not dead-code elimination — see mock-session.ts. A production build
  // cannot reach this because vite.config.ts refuses to build with mocks enabled.
  if (env.VITE_ENABLE_MOCKS) {
    const { hasMockSession, MOCK_ACCESS_TOKEN, MOCK_USER } = await import('./mock-session');
    if (hasMockSession()) {
      setBearerToken(MOCK_ACCESS_TOKEN);
      try {
        useSessionStore.getState().setAuthenticated(await loadUserProfile(fetchProfile));
      } catch {
        useSessionStore.getState().setAuthenticated(MOCK_USER);
      }
      resetRedirectAttempts();
      markReady();
      return;
    }
  }

  try {
    const config = await fetchAuthConfig();
    initOidc(config);
    setIdleTimeout(config.idleTimeoutSeconds);
  } catch (cause) {
    reportError(cause, { errorKind: 'auth_config_failed' });
    useSessionStore.getState().setError(i18n.t('Auth.SessionError.ConfigUnavailable'));
    markReady();
    return;
  }

  const { code, error } = readCallbackParams();

  if (error) {
    stripOAuthParams();
    reportError(new Error(`Cognito returned an OAuth error: ${error}`), {
      errorKind: 'oauth_error_response',
    });
    useSessionStore.getState().setError(i18n.t('Auth.SessionError.SignInFailed', { error }));
    markReady();
    return;
  }

  if (code) {
    await completeCallback(fetchProfile);
    markReady();
    return;
  }

  // Defense-in-depth: scrubs any stray `?code=`/`?error=` reaching a non-callback route (a
  // stale or bookmarked mid-flow URL) that was never exchanged. Runs before the router is
  // constructed, per stripOAuthParams' ordering requirement.
  stripStrayOAuthParams();

  // Before `cacheStoredToken`, so a session about to be revoked is never refreshed first. Only on
  // this stored-session path: before a callback exchange, a leftover stored user plus an old stamp
  // would log out a sign-in that had just succeeded.
  if (await isStoredSessionIdleExpired()) {
    // The full logout, not a local discard: only Cognito's /logout clears the Hosted UI cookie, so
    // discarding locally would let the guard's redirect sign the user silently back in — the exact
    // bypass (idling by closing the tab) this check exists to close.
    await signOutAndRevoke((error, stage) => {
      reportError(error, {
        errorKind: stage === 'revoke' ? 'revoke_failed' : 'session_remove_failed',
      });
    });
    // No markReady(): the browser is leaving for Cognito's /logout, and settling the session
    // would let a guard's authorize redirect race that assignment.
    return;
  }

  try {
    if (await cacheStoredToken()) {
      await adoptProfile(fetchProfile);
    } else {
      useSessionStore.getState().setAnonymous();
    }
  } catch {
    useSessionStore.getState().setAnonymous();
  }
  markReady();
}

async function isStoredSessionIdleExpired(): Promise<boolean> {
  const timeoutMs = getIdleTimeoutMs();
  if (timeoutMs === null) return false;
  if (!isIdleExpired(readLastActivity(), timeoutMs, Date.now())) return false;
  try {
    return (await getUserManager().getUser()) !== null;
  } catch {
    // Unreadable storage leaves `cacheStoredToken` to classify the session, as before.
    return false;
  }
}

async function completeCallback(fetchProfile: () => Promise<SessionUser>): Promise<void> {
  let user;
  try {
    user = await getUserManager().signinCallback();
  } catch {
    // A replayed or expired code lands here. Fall through to anonymous so the guard can
    // start a fresh redirect rather than crashing the boot.
    stripOAuthParams();
    useSessionStore.getState().setAnonymous();
    return;
  }

  // Before the router is constructed — see stripOAuthParams' doc comment.
  stripOAuthParams();

  if (!user?.access_token) {
    useSessionStore.getState().setAnonymous();
    return;
  }

  setBearerToken(user.access_token);
  // A new sign-in is fresh activity; without this it would be judged by the previous session's
  // stamp and logged out at the next boot.
  stampActivity();
  pendingReturnTo = safeReturnTo((user.state as { returnTo?: string } | undefined)?.returnTo);
  await adoptProfile(fetchProfile);
}

// Loads the backend profile once the session is authenticated. A failure here is deliberately not
// anonymous: the tokens are valid, so redirecting to Hosted UI would just repeat the same cycle
// with nothing reporting an error — the backend refusing an authenticated user is terminal.
async function adoptProfile(fetchProfile: () => Promise<SessionUser>): Promise<void> {
  try {
    const profile = await loadUserProfile(fetchProfile);
    useSessionStore.getState().setAuthenticated(profile);
    resetRedirectAttempts();
  } catch (cause) {
    // Reported explicitly: this path ends in a terminal screen rather than a thrown error,
    // so nothing else would ever tell anyone it happened. A refresh rejection is the exception:
    // endSession already captured it, and a second event would split one dead session in two.
    if (!(isAppError(cause) && cause.code === REFRESH_FAILED_CODE)) {
      reportError(cause, { errorKind: 'session_profile_failed' });
    }
    useSessionStore.getState().setError(i18n.t('Auth.SessionError.ProfileFailed'));
  }
}
